import React, { useEffect, useRef, useState } from "react";
import {
  Box,
  Image,
  Flex,
  Text,
  IconButton,
  Spinner,
  Tooltip,
  HStack,
} from "@chakra-ui/react";
import { FaDownload } from "react-icons/fa";
import type { Media } from "@twilio/conversations";
import {
  getFileIcon,
  formatFileSize,
  isImage,
  downloadMediaFile,
} from "../utils/fileUtils";

interface MediaDisplayProps {
  media: Media[];
  onImageClick?: (media: Media, blob: Blob) => void;
}

const MediaDisplay: React.FC<MediaDisplayProps> = ({ media, onImageClick }) => {
  const [mediaBlobs, setMediaBlobs] = useState<Map<string, Blob>>(new Map());
  const [loadingStates, setLoadingStates] = useState<Map<string, boolean>>(
    new Map()
  );
  const blobPromises = useRef<Map<string, Promise<Blob>>>(new Map()).current;
  const abortControllers = useRef<Map<string, AbortController>>(
    new Map()
  ).current;
  const objectUrls = useRef<Map<string, string>>(new Map()).current;

  useEffect(() => {
    const images = media.filter((m) => isImage(m.contentType));

    images.forEach((mediaItem) => {
      const sid = mediaItem.sid;
      if (!blobPromises.has(sid)) {
        setLoadingStates((prev) => new Map(prev).set(sid, true));

        const controller = new AbortController();
        abortControllers.set(sid, controller);
        blobPromises.set(
          sid,
          mediaItem
            .getContentTemporaryUrl()
            .then((url) => {
              if (!url) {
                throw new Error("No URL available");
              }
              return fetch(url, { signal: controller.signal });
            })
            .then((response) => {
              if (!response.ok) {
                throw new Error("Failed to fetch media");
              }
              return response.blob();
            })
            .then((blob) => {
              const objectUrl = URL.createObjectURL(blob);
              objectUrls.set(sid, objectUrl);
              return blob;
            })
        );

        blobPromises
          .get(sid)!
          .then((blob) => {
            setMediaBlobs((prev) => new Map(prev).set(sid, blob));
          })
          .catch((error) => {
            if (error.name === "AbortError") return;
            console.error(`Failed to load media ${sid}:`, error);
          })
          .finally(() => {
            setLoadingStates((prev) => new Map(prev).set(sid, false));
            abortControllers.delete(sid);
          });
      }
    });

    return () => {
      objectUrls.forEach((url) => URL.revokeObjectURL(url));
      objectUrls.clear();
      abortControllers.forEach((controller) => controller.abort());
      abortControllers.clear();
      blobPromises.clear();
      setMediaBlobs(new Map());
      setLoadingStates(new Map());
    };
  }, []); // Empty dependency array

  const handleDownload = downloadMediaFile;

  const images = media.filter((m) => isImage(m.contentType));
  const files = media.filter((m) => !isImage(m.contentType));

  return (
    <Box mt={2}>
      {/* Images */}
      {images.map((image) => {
        const blob = mediaBlobs.get(image.sid);
        const isLoading = loadingStates.get(image.sid);

        return (
          <Box key={image.sid} position="relative" mb={2}>
            {isLoading ? (
              <Flex
                align="center"
                justify="center"
                bg="gray.100"
                borderRadius="md"
                p={4}
                minH="100px"
              >
                <Spinner size="sm" color="blue.500" />
                <Text ml={2} fontSize="sm" color="gray.600">
                  Loading...
                </Text>
              </Flex>
            ) : blob ? (
              <Image
                src={objectUrls.get(image.sid) || URL.createObjectURL(blob)}
                alt={image.filename || "Image"}
                maxW="200px"
                maxH="200px"
                borderRadius="md"
                cursor="pointer"
                onClick={() => onImageClick?.(image, blob)}
                border="1px"
                borderColor="gray.200"
                _hover={{ opacity: 0.9 }}
              />
            ) : (
              <Flex
                align="center"
                justify="center"
                bg="red.50"
                borderRadius="md"
                p={4}
                minH="100px"
                border="1px"
                borderColor="red.200"
              >
                <Text fontSize="sm" color="red.600">
                  Failed to load image
                </Text>
              </Flex>
            )}
          </Box>
        );
      })}

      {/* Files */}
      {files.map((file) => {
        return (
          <Box key={file.sid} mb={2}>
            <Flex
              align="center"
              gap={3}
              bg="gray.50"
              borderRadius="md"
              p={3}
              border="1px"
              borderColor="gray.200"
              cursor="pointer"
              _hover={{ bg: "gray.100" }}
              onClick={() => handleDownload(file)}
            >
              {/* File Icon */}
              <Box flexShrink={0}>
                {React.createElement(
                  getFileIcon(file.contentType, file.filename || undefined)
                    .icon,
                  {
                    color: getFileIcon(
                      file.contentType,
                      file.filename || undefined
                    ).color,
                    size: "20px",
                  }
                )}
              </Box>

              {/* File Info */}
              <Box flex={1} minW={0}>
                <Text fontSize="sm" fontWeight="medium" noOfLines={1}>
                  {file.filename || "File"}
                </Text>
                <HStack spacing={2}>
                  <Text fontSize="xs" color="gray.600">
                    {formatFileSize(file.size)}
                  </Text>
                </HStack>
              </Box>

              {/* Download Icon */}
              <Box flexShrink={0}>
                <Tooltip label="Download">
                  <IconButton
                    aria-label="Download"
                    icon={<FaDownload />}
                    size="sm"
                    variant="ghost"
                    color="gray.600"
                    _hover={{ color: "blue.600" }}
                  />
                </Tooltip>
              </Box>
            </Flex>
          </Box>
        );
      })}
    </Box>
  );
};

export default MediaDisplay;